import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { authApi } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useToast } from '@/hooks/ui/use-toast'
import { ToastAction } from '@/components/ui/toast'
import type { LoginCredentials, SignupData } from '@/types/auth'
import type { UserProfileResponse } from '@/lib/api'

// TODO: Remove if no longer needed, or integrate into auth flow
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function _toUserProfile(response: UserProfileResponse): Omit<UserProfileResponse, 'user'> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user, ...profile } = response
  return profile
}

/**
 * Hook for handling user login authentication
 * @returns Mutation object with login function, loading state, and error handling
 * @example
 * ```tsx
 * const login = useLogin()
 * await login.mutateAsync({ username: 'user', password: 'pass' })
 * ```
 */
export function useLogin() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      await authApi.csrf()
      const response = await authApi.login(credentials)
      return response.data
    },
    onSuccess: (data) => {
      login(data.user)

      toast({
        title: 'Welcome back!',
        description: `Logged in as ${data.user.username}`,
      })

      const returnTo = new URLSearchParams(window.location.search).get('returnTo')
      navigate(returnTo || '/dashboard', { replace: true })
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.non_field_errors?.[0] ||
        'Login failed. Please check your credentials.'
      toast({
        variant: 'destructive',
        title: 'Login failed',
        description: message,
      })
    },
  })
}

/**
 * Hook for handling user registration
 * @returns Mutation object with signup function, automatic login on success
 * @example
 * ```tsx
 * const signup = useSignup()
 * await signup.mutateAsync({ username: 'user', password: 'pass', email: 'user@example.com' })
 * ```
 */
export function useSignup() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: SignupData) => {
      await authApi.csrf()
      const { accountType, confirmPassword, ...frontendData } = data as any
      const signupData = {
        ...frontendData,
        account_type: accountType,
        confirm_password: confirmPassword,
      }
      const response = await authApi.signup(signupData)
      return response.data
    },
    onSuccess: (data) => {
      login(data.user)

      toast({
        title: 'Welcome to Masonyt!',
        description: `Account created for ${data.user.username}`,
      })
      navigate('/dashboard', { replace: true })
    },
    onError: (error: any) => {
      const errorData = error.response?.data
      let description = 'Registration failed. Please try again.'
      let hasExistingAccount = false

      if (errorData?.username) {
        description = `Username: ${errorData.username[0]}`
        hasExistingAccount = errorData.username[0].includes('already exists')
      } else if (errorData?.email) {
        description = `Email: ${errorData.email[0]}`
        hasExistingAccount = errorData.email[0].includes('already exists')
      } else if (errorData?.password) {
        description = `Password: ${errorData.password[0]}`
      } else if (errorData?.confirm_password) {
        description = `Confirm Password: ${errorData.confirm_password[0]}`
      } else if (errorData?.non_field_errors) {
        description = errorData.non_field_errors[0]
      }

      toast({
        variant: 'destructive',
        title: 'Registration failed',
        description,
        action: hasExistingAccount ? (
          <ToastAction altText="Go to sign in" onClick={() => navigate('/login')}>
            Sign in
          </ToastAction>
        ) : undefined,
      })
    },
  })
}
